import {
  inventoryMovementAlertEmailsUrl,
  inventoryMovementProductsLookupUrl,
  inventoryMovementProductsUrl,
  inventoryMovementsUrl
} from '../../utils/ConstantUtils'
import { getApiInstance } from '../axiosInterceptor'
import { formatTimestamp } from '../../pages/inventoryMovement/inventoryMovementConstants'

export const getApiErrorMessage = (error, fallback) => error?.response?.data?.error || fallback

const toUiProduct = product => ({
  keycode: product.keyCode,
  productName: product.productDescription || ''
})

// Maps the backend movement (movementId, lines[]) to the shape the inventory movement views render.
export const toUiMovement = movement => ({
  id: movement.movementId,
  reference: movement.movementId,
  source: movement.source,
  createdBy: movement.createdBy,
  createdAt: movement.createdAt ? formatTimestamp(new Date(movement.createdAt)) : '',
  notes: movement.notes || '',
  products: (movement.lines || []).map(line => ({
    keycode: line.keyCode,
    productName: line.productDescription || '',
    quantity: line.quantity,
    fromMovementStatus: line.fromMovementStatus,
    toMovementStatus: line.toMovementStatus,
    dsvFromStatus: line.dsvFromStatus,
    dsvStatus: line.dsvStatus,
    dsvQuantity: line.dsvQuantity,
    reasonCode: line.reasonCode || '',
    reasonName: line.reasonName || '',
    note: line.note || ''
  }))
})

export const fetchInventoryMovements = async (instance, { page, limit, keyCode, productName, fromMovementStatus, toMovementStatus, dsvStatus }) => {
  const params = { page, limit }
  if (keyCode) params.keyCode = keyCode
  if (productName) params.productName = productName
  if (fromMovementStatus) params.fromMovementStatus = fromMovementStatus
  if (toMovementStatus) params.toMovementStatus = toMovementStatus
  if (dsvStatus) params.dsvStatus = dsvStatus

  const response = await getApiInstance(instance).get(inventoryMovementsUrl, { params })
  return {
    movements: (response.data?.movements || []).map(toUiMovement),
    total: response.data?.pagination?.total || 0
  }
}

export const fetchInventoryMovement = async (instance, movementId) => {
  const response = await getApiInstance(instance).get(`${inventoryMovementsUrl}/${encodeURIComponent(movementId)}`)
  return toUiMovement(response.data.movement)
}

export const createInventoryMovement = async (instance, { notes, products }) => {
  const response = await getApiInstance(instance).post(inventoryMovementsUrl, {
    notes,
    products: products.map(product => ({
      keyCode: product.keycode,
      quantity: product.quantity,
      fromMovementStatus: product.fromMovementStatus,
      toMovementStatus: product.toMovementStatus,
      reasonCode: product.reasonCode,
      note: product.note
    }))
  })
  return {
    movement: toUiMovement(response.data.movement),
    emailSent: response.data.emailSent === true
  }
}

export const searchInventoryProducts = async (instance, search, limit = 20) => {
  const response = await getApiInstance(instance).get(inventoryMovementProductsUrl, { params: { search, limit } })
  return (response.data?.products || []).map(toUiProduct)
}

export const lookupInventoryProducts = async (instance, keyCodes) => {
  const response = await getApiInstance(instance).post(inventoryMovementProductsLookupUrl, { keyCodes })
  return {
    products: (response.data?.products || []).map(toUiProduct),
    notFound: response.data?.notFound || []
  }
}

export const fetchAlertEmails = async (instance) => {
  const response = await getApiInstance(instance).get(inventoryMovementAlertEmailsUrl)
  return response.data?.alertEmails || []
}

export const addAlertEmails = async (instance, emailIds) => {
  const response = await getApiInstance(instance).post(inventoryMovementAlertEmailsUrl, { emailIds })
  return response.data?.alertEmails || []
}

export const deleteAlertEmail = async (instance, id) => {
  const response = await getApiInstance(instance).delete(`${inventoryMovementAlertEmailsUrl}/${encodeURIComponent(id)}`)
  return response.data?.alertEmails || []
}
